import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { promoCodeManagementService, PromoCode, ListPromoCodesParams } from '../services/promoCodeManagement';
import { Table, TableColumn } from '../components/Table';
import './PromoCodesPage.css';

export const PromoCodesTab: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  const [viewingUsage, setViewingUsage] = useState<string | null>(null);
  const itemsPerPage = 50;

  const loadPromoCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListPromoCodesParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await promoCodeManagementService.listPromoCodes(params);
      setPromoCodes(response.promoCodes);
      setTotalCount(response.count);
      setLastEvaluatedKey(
        response.lastEvaluatedKey
          ? encodeURIComponent(JSON.stringify(response.lastEvaluatedKey))
          : undefined
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load promo codes');
      console.error('Error loading promo codes:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lastEvaluatedKey intentionally omitted
  }, [currentPage, statusFilter, searchTerm]);

  useEffect(() => {
    loadPromoCodes();
  }, [loadPromoCodes]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadPromoCodes();
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleDelete = useCallback(async (promoCodeId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this promo code?')) {
      return;
    }

    try {
      await promoCodeManagementService.deletePromoCode(promoCodeId);
      await loadPromoCodes();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete promo code');
    }
  }, [loadPromoCodes]);

  const handleExport = () => {
    const csvContent = [
      ['Promo Code', 'Description', 'Discount Type', 'Discount Value', 'Status', 'Zip Codes', 'Usage', 'Max Usage', 'Expiry Date', 'Created'].join(','),
      ...promoCodes.map(pc => [
        pc.promoCode,
        pc.description || '',
        pc.discountType,
        pc.discountValue,
        pc.status,
        (pc.zipCodes || []).join(';'),
        pc.currentUsage,
        pc.maxUsage || 'Unlimited',
        pc.expiryDate || '',
        pc.createdAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo-codes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: TableColumn<PromoCode>[] = useMemo(() => [
    {
      header: 'Promo Code',
      accessor: 'promoCode',
      render: (value) => (
        <div className="promo-code-id">{value as string}</div>
      ),
    },
    {
      header: 'Description',
      accessor: 'description',
      render: (value) => (value as string) || '-',
      hideOnTablet: true,
    },
    {
      header: 'Discount',
      accessor: (row) => {
        if (row.discountType === 'percentage') {
          return `${row.discountValue}%${row.maxDiscount ? ` (max ₹${row.maxDiscount})` : ''}`;
        }
        return `₹${row.discountValue}`;
      },
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => (
        <span className={`status-badge ${value as string}`}>
          {value as string}
        </span>
      ),
    },
    {
      header: 'Zip Scope',
      accessor: (row) => (row.zipCodes && row.zipCodes.length > 0 ? row.zipCodes.join(', ') : 'All'),
      render: (value) => (value as string).length > 30 ? `${(value as string).slice(0, 30)}...` : (value as string),
      hideOnMobile: true,
    },
    {
      header: 'Usage',
      accessor: (row) => `${row.currentUsage} / ${row.maxUsage || '∞'}`,
    },
    {
      header: 'Expiry Date',
      accessor: 'expiryDate',
      render: (value) => formatDate(value as string | undefined),
      hideOnTablet: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <div className="action-buttons">
          <button
            className="view-button"
            onClick={() => setViewingUsage(row.promoCode)}
          >
            Usage
          </button>
          <button
            className="edit-button"
            onClick={() => setEditingCode(row)}
          >
            Edit
          </button>
          <button
            className="delete-button"
            onClick={() => handleDelete(row.promoCode)}
          >
            Delete
          </button>
        </div>
      ),
      dataLabel: '',
    },
  ], [handleDelete]);

  return (
    <div className="promo-codes-tab">
      <div className="promo-codes-actions">
        <button onClick={() => setShowCreateModal(true)} className="create-button">
          Create Promo Code
        </button>
        <button onClick={handleExport} className="export-button">
          Export CSV
        </button>
      </div>

      <div className="promo-codes-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by promo code or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="filter-buttons">
          <button
            className={`filter-button ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('active')}
          >
            Active
          </button>
          <button
            className={`filter-button ${statusFilter === 'inactive' ? 'active' : ''}`}
            onClick={() => handleStatusFilter('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading promo codes...</div>
      ) : (
        <>
          <div className="promo-codes-stats">
            <div className="stat-card">
              <div className="stat-value">{totalCount}</div>
              <div className="stat-label">Total Promo Codes</div>
            </div>
          </div>

          <Table
            columns={columns}
            data={promoCodes}
            getRowId={(row) => row.promoCode}
            emptyMessage="No promo codes found"
            containerClassName="promo-codes-table-container"
            className="promo-codes-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || promoCodes.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {showCreateModal && (
        <CreatePromoCodeModal
          onClose={async () => {
            setShowCreateModal(false);
            await loadPromoCodes();
          }}
        />
      )}

      {editingCode && (
        <EditPromoCodeModal
          promoCode={editingCode}
          onClose={async () => {
            setEditingCode(null);
            await loadPromoCodes();
          }}
        />
      )}

      {viewingUsage && (
        <PromoCodeUsageModal
          promoCodeId={viewingUsage}
          onClose={() => setViewingUsage(null)}
        />
      )}
    </div>
  );
};

const CreatePromoCodeModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    promoCode: '',
    description: '',
    discountType: 'percentage' as 'percentage' | 'fixed',
    discountValue: '',
    maxDiscount: '',
    minBookingAmount: '',
    maxUsage: '',
    expiryDate: '',
    startDate: '',
    applicableServices: '',
    zipCodes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await promoCodeManagementService.createPromoCode({
        promoCode: formData.promoCode,
        description: formData.description || undefined,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : undefined,
        minBookingAmount: formData.minBookingAmount ? parseFloat(formData.minBookingAmount) : undefined,
        maxUsage: formData.maxUsage ? parseInt(formData.maxUsage, 10) : undefined,
        expiryDate: formData.expiryDate || undefined,
        startDate: formData.startDate || undefined,
        applicableServices: formData.applicableServices
          ? formData.applicableServices.split(',').map(s => s.trim()).filter(Boolean)
          : undefined,
        zipCodes: formData.zipCodes
          ? formData.zipCodes.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
          : undefined,
      });
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to create promo code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Create Promo Code</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Promo Code *</label>
            <input
              type="text"
              value={formData.promoCode}
              onChange={(e) => setFormData({ ...formData, promoCode: e.target.value.toUpperCase() })}
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Discount Type *</label>
            <select
              value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value as 'percentage' | 'fixed' })}
              required
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>
          <div className="form-group">
            <label>Discount Value *</label>
            <input
              type="number"
              step="0.01"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
              required
            />
          </div>
          {formData.discountType === 'percentage' && (
            <div className="form-group">
              <label>Max Discount (₹)</label>
              <input
                type="number"
                step="0.01"
                value={formData.maxDiscount}
                onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
              />
            </div>
          )}
          <div className="form-group">
            <label>Min Booking Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              value={formData.minBookingAmount}
              onChange={(e) => setFormData({ ...formData, minBookingAmount: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Max Usage</label>
            <input
              type="number"
              value={formData.maxUsage}
              onChange={(e) => setFormData({ ...formData, maxUsage: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Zip Scope (comma-separated, empty = all)</label>
            <input
              type="text"
              value={formData.zipCodes}
              onChange={(e) => setFormData({ ...formData, zipCodes: e.target.value })}
              placeholder="560001, 560002, 560003"
            />
          </div>
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Expiry Date</label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Applicable Services (comma-separated)</label>
            <input
              type="text"
              value={formData.applicableServices}
              onChange={(e) => setFormData({ ...formData, applicableServices: e.target.value })}
              placeholder="cleaning, plumbing, electrical"
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditPromoCodeModal: React.FC<{ promoCode: PromoCode; onClose: () => void }> = ({ promoCode, onClose }) => {
  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return dateString;
    }
  };

  const [formData, setFormData] = useState({
    description: promoCode.description || '',
    discountType: promoCode.discountType,
    discountValue: promoCode.discountValue.toString(),
    maxDiscount: promoCode.maxDiscount?.toString() || '',
    minBookingAmount: promoCode.minBookingAmount?.toString() || '',
    maxUsage: promoCode.maxUsage?.toString() || '',
    expiryDate: formatDateForInput(promoCode.expiryDate),
    startDate: formatDateForInput(promoCode.startDate),
    applicableServices: promoCode.applicableServices?.join(', ') || '',
    zipCodes: promoCode.zipCodes?.join(', ') || '',
    status: promoCode.status,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await promoCodeManagementService.updatePromoCode(promoCode.promoCode, {
        description: formData.description || undefined,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : undefined,
        minBookingAmount: formData.minBookingAmount ? parseFloat(formData.minBookingAmount) : undefined,
        maxUsage: formData.maxUsage ? parseInt(formData.maxUsage, 10) : undefined,
        expiryDate: formData.expiryDate || undefined,
        startDate: formData.startDate || undefined,
        applicableServices: formData.applicableServices
          ? formData.applicableServices.split(',').map(s => s.trim()).filter(s => s.length > 0)
          : undefined,
        zipCodes: formData.zipCodes
          ? formData.zipCodes.split(/[,\s]+/).map(s => s.trim()).filter(Boolean)
          : undefined,
        status: formData.status as 'active' | 'inactive',
      });
      await onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update promo code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Promo Code: {promoCode.promoCode}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Discount Type *</label>
            <select
              value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value as 'percentage' | 'fixed' })}
              required
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
          </div>
          <div className="form-group">
            <label>Discount Value *</label>
            <input
              type="number"
              step="0.01"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
              required
            />
          </div>
          {formData.discountType === 'percentage' && (
            <div className="form-group">
              <label>Max Discount (₹)</label>
              <input
                type="number"
                step="0.01"
                value={formData.maxDiscount}
                onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value })}
              />
            </div>
          )}
          <div className="form-group">
            <label>Min Booking Amount (₹)</label>
            <input
              type="number"
              step="0.01"
              value={formData.minBookingAmount}
              onChange={(e) => setFormData({ ...formData, minBookingAmount: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Max Usage</label>
            <input
              type="number"
              value={formData.maxUsage}
              onChange={(e) => setFormData({ ...formData, maxUsage: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Zip Scope (comma-separated, empty = all)</label>
            <input
              type="text"
              value={formData.zipCodes}
              onChange={(e) => setFormData({ ...formData, zipCodes: e.target.value })}
              placeholder="560001, 560002"
            />
          </div>
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Expiry Date</label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="form-group">
            <label>Applicable Services (comma-separated)</label>
            <input
              type="text"
              value={formData.applicableServices}
              onChange={(e) => setFormData({ ...formData, applicableServices: e.target.value })}
              placeholder="cleaning, plumbing, electrical"
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface PromoCodeUsageStat {
  promoCode: { promoCode: string };
  usageCount: number;
  totalDiscounts: number;
  uniqueUsers: number;
  usages: Array<{ usageId: string; customerId: string; bookingId: string; originalAmount: number; discountAmount: number; finalAmount: number; usedAt: string }>;
}

const PromoCodeUsageModal: React.FC<{ promoCodeId: string; onClose: () => void }> = ({ promoCodeId, onClose }) => {
  const [usageStats, setUsageStats] = useState<PromoCodeUsageStat | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUsageStats = async () => {
      try {
        setLoading(true);
        const stats = await promoCodeManagementService.getPromoCodeUsage(promoCodeId);
        setUsageStats(stats as PromoCodeUsageStat);
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Failed to load usage stats');
      } finally {
        setLoading(false);
      }
    };
    loadUsageStats();
  }, [promoCodeId]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (!usageStats) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content usage-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Usage Statistics: {usageStats.promoCode.promoCode}</h2>
        <div className="usage-stats">
          <div className="stat-item">
            <div className="stat-label">Total Usage</div>
            <div className="stat-value">{usageStats.usageCount}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Total Discounts</div>
            <div className="stat-value">₹{usageStats.totalDiscounts.toLocaleString()}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Unique Users</div>
            <div className="stat-value">{usageStats.uniqueUsers}</div>
          </div>
        </div>
        <div className="usage-list">
          <h3>Usage History</h3>
          <table>
            <thead>
              <tr>
                <th>Customer ID</th>
                <th>Booking ID</th>
                <th>Original Amount</th>
                <th>Discount</th>
                <th>Final Amount</th>
                <th>Used At</th>
              </tr>
            </thead>
            <tbody>
              {usageStats.usages.map((usage) => (
                <tr key={usage.usageId}>
                  <td>{usage.customerId.substring(0, 8)}...</td>
                  <td>{usage.bookingId.substring(0, 8)}...</td>
                  <td>₹{usage.originalAmount}</td>
                  <td>₹{usage.discountAmount}</td>
                  <td>₹{usage.finalAmount}</td>
                  <td>{new Date(usage.usedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
